import React, { useState, useMemo, useContext, useEffect } from 'react';
import { arrayMoveImmutable } from 'array-move';
import {
  Drawer,
  Input,
  Row,
  Col,
  Tabs,
  Button,
  Spin,
  Table,
  message,
  Modal,
  Popover,
  Empty as AntdEmpty,
} from 'antd';
import {
  MinusCircleFilled,
  PlusCircleFilled,
  CheckCircleFilled,
  MenuOutlined,
  ArrowUpOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { DndContext, PointerSensor, useSensor, useSensors, closestCorners } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

import { DashboardStore } from '@/stores';
import { Icon } from '@/components';
import Empty from '@/components/Empty';

import type { ColumnsType } from 'antd/es/table';
import type { DragEndEvent } from '@dnd-kit/core';

import './index.less';
import { ApplicationInfo } from '@/const/application';
import theme from '@/theme';

export interface EntranceItem {
  id: number;
  name: string;
  quickIcon: string;
  path: string;
  functionId: number;
  redirect: string;
  target: string;
}

interface AllEntryListItem {
  id: number;
  name: string;
  menuList: EntranceItem[];
}

interface EntranceSettingProps {
  open: boolean;
  usageEntryList: EntranceItem[];
  onClose?: () => void;
  onLoad?: () => void;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
  children: any;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const TableRow = (props: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<MenuOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

export default (props: EntranceSettingProps) => {
  const { usageEntryList, onClose, open, onLoad } = props;
  const { systemId } = ApplicationInfo;

  const [isChange, setIsChange] = useState<boolean>(false);
  const [usageFilterKey, setUsageFilterKey] = useState<string>();
  const [usageList, setUsageList] = useState<EntranceItem[]>([]);
  const [activeKey, setActiveKey] = useState<string>();
  const [allFilterKey, setAllFilterKey] = useState<string>();

  useEffect(() => {
    if (open) {
      setIsChange(false);
      setUsageFilterKey('');
      setAllFilterKey('');
      setUsageList(usageEntryList);
    }
  }, [usageEntryList, open]);

  const usageFilterList = useMemo(() => {
    return usageList.filter((item) => {
      return item.name.includes(usageFilterKey || '');
    });
  }, [usageList, usageFilterKey]);

  const {
    data: allEntryList,
    loading: allEntryLoading,
    run: getQuickMenuAllList,
  } = useRequest<AllEntryListItem[], []>(
    async () => {
      const { data } = await DashboardStore.getQuickMenuAllList({
        systemId,
        menuName: allFilterKey,
      });
      setActiveKey(`${data[0]?.id}`);

      return data || [];
    },
    {
      manual: true,
      debounceWait: 300,
      loadingDelay: 300,
    },
  );

  useEffect(() => {
    open && getQuickMenuAllList();
  }, [allFilterKey, open]);

  const addEntry = (item: EntranceItem) => {
    setUsageList([...usageList, item]);
  };

  const removeEntry = (item: EntranceItem) => {
    setUsageList(usageList.filter((usageItem) => usageItem.id !== item.id));
  };

  const [childrenDrawer, setChildrenDrawer] = useState(false);

  const columns: ColumnsType<EntranceItem> = [
    {
      title: '拖拽排序',
      dataIndex: 'sort',
      width: 60,
      align: 'center',
      render: () => <DragHandle />,
    },
    {
      title: '常用入口',
      dataIndex: 'quickEntry',
      render: (_, record) => (
        <div className="entrance-drag-item">
          <img src={record.quickIcon} />
          <span>{record.name}</span>
        </div>
      ),
    },
    {
      title: '置顶',
      dataIndex: 'backtop',
      className: 'drag-visible',
      render: (_, record) => (
        <Button
          size="small"
          type="text"
          icon={<ArrowUpOutlined />}
          onClick={() => {
            if (usageList[0].id !== record.id) {
              const oldIndex = usageList.findIndex((item) => item.id === record.id);
              const newIndex = usageList.findIndex((item) => item.id === usageList[0].id);

              const next = arrayMoveImmutable(usageList, oldIndex, newIndex);

              setUsageList(next);
            }
          }}
        />
      ),
    },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = usageList.findIndex((item) => item.id === active.id);
      const newIndex = usageList.findIndex((item) => item.id === over?.id);

      const next = arrayMoveImmutable(usageList, oldIndex, newIndex);

      setUsageList(next);
      !isChange && setIsChange(true);
    }
  };

  const { run: saveQuickMenu, loading: saveLoading } = useRequest(
    async () => {
      const { success } = await DashboardStore.saveQuickMenu({
        systemId,
        functionIdList: usageList.map((item) => item.functionId), // 调整成新的唯一编码
        // menuIdList: usageList.map((item) => item.id),
      });

      if (success) {
        message.success('保存成功');
        onLoad && onLoad();
        onClose && onClose();
      }
    },
    {
      loadingDelay: 300,
      manual: true,
    },
  );

  const handleClose = () => {
    if (isChange) {
      Modal.confirm({
        title: '提示',
        content: '您有未保存的操作，是否需要保存？',
        okText: '帮我保存',
        cancelText: '不保存',
        okButtonProps: {
          style: { backgroundColor: '#eb0045', borderColor: '#eb0045' }
        },
        cancelButtonProps: {
          className: 'confirm-cancel-self-btn'
        },
        onOk: () => {
          saveQuickMenu();
        },
        onCancel: () => {
          onClose && onClose();
        },
      });
    } else {
      onClose && onClose();
    }
  };

  return (
    <Drawer
      open={open}
      width={760}
      title="快捷入口配置"
      className="entrance-setting-wrap"
      styles={{ body: { padding: 0 } }}
      onClose={handleClose}
      maskClosable={false}
      extra={
        <Button type="primary" onClick={saveQuickMenu} loading={saveLoading}>
          保存操作
        </Button>
      }
    >
      {allEntryLoading && (
        <Spin
          spinning
          style={{
            position: 'absolute',
            top: '50%',
            height: '50%',
            zIndex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          }}
        />
      )}
      <div className="entrance-setting-body">
        <div className="entrance-module">
          <div className="entrance-flex entrance-direction-column">
            <Row className="entrance-module-header" align="middle">
              <Col flex={1}>
                <span className="entrance-module-title">我的入口</span>
                <Button type="link" size="small" onClick={() => setChildrenDrawer(true)}>
                  <Icon type="icon-paixu" />
                  <span style={{ marginLeft: 4 }}>排序</span>
                </Button>
              </Col>
              <Col>
                <Input
                  allowClear
                  size="small"
                  value={usageFilterKey}
                  placeholder="请输入菜单名称"
                  prefix={<SearchOutlined />}
                  style={{ fontSize: 12, width: 140, height: 24 }}
                  onChange={(e) => setUsageFilterKey(e.target.value)}
                />
              </Col>
            </Row>
            <div className="entrance-module-body entrance-flex entrance-flex-wrap fle-scroll">
              {usageFilterList.map((item) => (
                <div className="entrance-item" key={item.id}>
                  <div className="entrance-item-icon">
                    <MinusCircleFilled
                      className="entrance-close-icon"
                      onClick={() => {
                        removeEntry(item);
                        !isChange && setIsChange(true);
                      }}
                    />
                    <img src={item.quickIcon} />
                  </div>
                  <div className="entrance-item-title">
                    <Popover placement="bottom" content={item.name}>
                      {item.name}
                    </Popover>
                  </div>
                </div>
              ))}
              {usageFilterList.length === 0 && (
                <AntdEmpty
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
                  image={
                    <img
                      style={{ width: 120, height: 120,}}
                      src="https://oss.elebuys.com/resource/goods/materials/202404131402380003387150.png"
                    />
                  }
                  description={<div style={{ marginTop: 24, color: '#aaa' }}>暂无入口</div>}
                />
              )}
            </div>
          </div>
        </div>

        <div className="entrance-module">
          <div className="entrance-flex entrance-direction-column">
            <div className="entrance-module-body entrance-flex entrance-direction-column">
              <Row className="entrance-module-header" align="middle">
                <Col flex={1}>
                  <span className="entrance-module-title">全部入口</span>
                </Col>
                <Col>
                  <Input
                    allowClear
                    size="small"
                    value={allFilterKey}
                    placeholder="请输入菜单名称"
                    prefix={<SearchOutlined />}
                    style={{ fontSize: 12, width: 140, height: 24 }}
                    onChange={(e) => setAllFilterKey(e.target.value)}
                  />
                </Col>
              </Row>
              {allEntryList && allEntryList?.length > 0 && (
                <div className="entrance-wrap">
                  <Tabs
                    size="small"
                    tabPosition="top"
                    activeKey={activeKey}
                    onChange={setActiveKey}
                    className="entrance-tabs"
                    tabBarStyle={{ height: 46 }}
                    items={allEntryList?.map((item) => {
                      return {
                        label: item.name,
                        key: `${item.id}`,
                        children: (
                          <Row wrap className="fle-scroll" style={{ alignContent: 'flex-start' }}>
                            {item.menuList.map((item) => (
                              <Col className="entrance-item" key={item.id}>
                                <div className="entrance-item-icon">
                                  {/* TODO：这一块要做一下缓存 */}
                                  {usageFilterList.find((usageItem) => usageItem.id === item.id) ? (
                                    <CheckCircleFilled className="entrance-check-icon" />
                                  ) : (
                                    <PlusCircleFilled
                                      className="entrance-add-icon"
                                      onClick={() => {
                                        addEntry(item);
                                        !isChange && setIsChange(true);
                                      }}
                                    />
                                  )}
                                  <img src={item.quickIcon} />
                                </div>
                                <div className="entrance-item-title">
                                  {' '}
                                  <Popover placement="bottom" content={item.name}>
                                    {item.name}
                                  </Popover>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ),
                      };
                    })}
                  />
                </div>
              )}

              {allEntryList?.length === 0 && (
                <Empty description={<div style={{ marginTop: 16, color: '#aaa' }}>暂无入口</div>} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 常用入口排序 */}
      <Drawer
        width={320}
        closable={false}
        open={childrenDrawer}
        title="快捷入口排序"
        bodyStyle={{ padding: 0 }}
        onClose={() => setChildrenDrawer(false)}
      >
        <div className="fle-scroll">
          <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
            collisionDetection={closestCorners}
          >
            <SortableContext
              items={usageList.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                size="small"
                showHeader={false}
                pagination={false}
                columns={columns}
                dataSource={usageList}
                className="entrance-drag-table"
                locale={{
                  emptyText: (
                    <Empty
                      description={
                        <div style={{ marginTop: '24px', color: '#aaa' }}>暂无常用入口</div>
                      }
                    />
                  ),
                }}
                rowKey="id"
                components={{
                  body: {
                    row: TableRow,
                  },
                }}
              />
            </SortableContext>
          </DndContext>
        </div>
      </Drawer>
    </Drawer>
  );
};
